import { createContext, type ReactNode, useContext } from 'react';
import type { ResponseError } from 'superagent';

import type { TutorViewFull } from '@didaskill/domain/users/types';
import { useMemoizedBundle } from '@didaskill/ui/utils';

import { useUser } from '../hooks/queries/user';

export type AuthContextValue = {
  user: TutorViewFull | undefined;
  isLoading: boolean;
  error?: Error | ResponseError;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: false,
  error: null,
});

export type AuthProviderProps = {
  children?: ReactNode;
};

const AuthProvider = ({ children = null }: AuthProviderProps) => {
  const {
    data: user,
    isLoading,
    error,
  } = useUser({
    throwOnError: false,
  });

  const contextValue = useMemoizedBundle<AuthContextValue>({
    user,
    isLoading,
    error,
  });

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export { AuthProvider, useAuth };
